import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
}

@Component({
  selector: 'app-l1-interview',
  templateUrl: './l1-interview.component.html',
  styleUrls: ['./l1-interview.component.scss'],
  standalone: true,
  imports:[CommonModule]
})
export class L1InterviewComponent implements OnInit, OnDestroy {
  // Template helper
  public String = String;

  // Interview state
  public interviewStarted = false;
  public currentQuestionIndex = 0;
  public selectedAnswer: number | null = null;
  public answerSubmitted = false;
  public timeRemaining = 15;
  public score = 0;
  public interviewCompleted = false;

  // Store user responses
  private userResponses: Array<{
    questionId: number;
    question: string;
    selectedOption: string | null;
    correctOption: string;
    isCorrect: boolean;
    timeSpent: number;
  }> = [];

  // Timer management
  private destroy$ = new Subject<void>();
  private timerSubscription$ = new Subject<void>();

  // Interview information
  public interviewInfo = {
    position: 'Java Backend Developer',
    company: 'Gigsumo',
    duration: '15 seconds per question',
    totalQuestions: 15,
    passingScore: 10
  };

  // Questions bank
  public questions: Question[] = [
    {
      id: 1,
      question: 'Which of the following is NOT a feature of Java?',
      options: [
        'Object-Oriented',
        'Platform Independent',
        'Pointer-based',
        'Multithreaded'
      ],
      correctAnswer: 2
    },
    {
      id: 2,
      question: 'What is the default value of a boolean variable in Java?',
      options: [
        'true',
        'false',
        'null',
        '0'
      ],
      correctAnswer: 1
    },
    {
      id: 3,
      question: 'Which annotation is used to map HTTP GET requests in Spring Boot?',
      options: [
        '@RequestMapping',
        '@GetMapping',
        '@PostMapping',
        '@RestController'
      ],
      correctAnswer: 1
    },
    {
      id: 4,
      question: 'What does JPA stand for in Spring Framework?',
      options: [
        'Java Persistence API',
        'Java Programming API',
        'Java Processing Application',
        'Java Platform Architecture'
      ],
      correctAnswer: 0
    },
    {
      id: 5,
      question: 'Which interface is the root of the Java Collection Framework?',
      options: [
        'List',
        'Set',
        'Collection',
        'Map'
      ],
      correctAnswer: 2
    },
    {
      id: 6,
      question: 'What is the purpose of the @Autowired annotation in Spring?',
      options: [
        'To create a new bean',
        'To enable dependency injection',
        'To map HTTP requests',
        'To configure database connection'
      ],
      correctAnswer: 1
    },
    {
      id: 7,
      question: 'Which keyword is used to prevent method overriding in Java?',
      options: [
        'static',
        'final',
        'abstract',
        'synchronized'
      ],
      correctAnswer: 1
    },
    {
      id: 8,
      question: 'What is the default transaction propagation in Spring?',
      options: [
        'REQUIRES_NEW',
        'REQUIRED',
        'NESTED',
        'MANDATORY'
      ],
      correctAnswer: 1
    },
    {
      id: 9,
      question: 'Which of these is NOT a valid access modifier in Java?',
      options: [
        'public',
        'protected',
        'package',
        'private'
      ],
      correctAnswer: 2
    },
    {
      id: 10,
      question: 'What does REST stand for?',
      options: [
        'Representational State Transfer',
        'Remote State Transfer',
        'Representational System Transfer',
        'Resource State Transfer'
      ],
      correctAnswer: 0
    },
    {
      id: 11,
      question: 'Which annotation marks a class as a Spring REST controller?',
      options: [
        '@Controller',
        '@Service',
        '@RestController',
        '@Component'
      ],
      correctAnswer: 2
    },
    {
      id: 12,
      question: 'What is the difference between HashMap and ConcurrentHashMap?',
      options: [
        'HashMap is faster',
        'ConcurrentHashMap is thread-safe',
        'HashMap allows null keys',
        'No difference'
      ],
      correctAnswer: 1
    },
    {
      id: 13,
      question: 'Which HTTP method is idempotent?',
      options: [
        'POST',
        'PATCH',
        'PUT',
        'All of the above'
      ],
      correctAnswer: 2
    },
    {
      id: 14,
      question: 'What is the purpose of the @Transactional annotation?',
      options: [
        'To handle HTTP transactions',
        'To manage database transactions',
        'To create transactional beans',
        'To configure transaction timeout'
      ],
      correctAnswer: 1
    },
    {
      id: 15,
      question: 'Which design pattern is commonly used in Spring Framework?',
      options: [
        'Singleton Pattern',
        'Dependency Injection',
        'Factory Pattern',
        'All of the above'
      ],
      correctAnswer: 3
    }
  ];

  constructor() {}

  ngOnInit(): void {
    // Component initialization
  }

  ngOnDestroy(): void {
    this.stopTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Starts the interview and initializes the first question
   */
  public startInterview(): void {
    this.interviewStarted = true;
    this.currentQuestionIndex = 0;
    this.score = 0;
    this.interviewCompleted = false;
    this.userResponses = [];
    this.loadQuestion();
  }

  /**
   * Loads the current question and starts the timer
   */
  private loadQuestion(): void {
    this.selectedAnswer = null;
    this.answerSubmitted = false;
    this.timeRemaining = 15;
    this.startTimer();
  }

  /**
   * Starts the countdown timer for the current question
   */
  private startTimer(): void {
    this.timerSubscription$ = new Subject<void>();

    interval(1000).pipe(
      takeUntil(this.timerSubscription$),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.timeRemaining--;

      if (this.timeRemaining <= 0) {
        this.autoSubmitAnswer();
      }
    });
  }

  /**
   * Stops the current timer
   */
  private stopTimer(): void {
    this.timerSubscription$.next();
    this.timerSubscription$.complete();
  }

  /**
   * Handles option selection by the user
   * @param index - The index of the selected option
   */
  public selectOption(index: number): void {
    if (!this.answerSubmitted) {
      this.selectedAnswer = index;
    }
  }

  /**
   * Submits the selected answer
   */
  public submitAnswer(): void {
    if (this.selectedAnswer === null || this.answerSubmitted) {
      return;
    }

    this.stopTimer();
    this.answerSubmitted = true;

    // Calculate time spent on this question
    const timeSpent = 15 - this.timeRemaining;

    // Check if answer is correct
    const isCorrect = this.selectedAnswer === this.currentQuestion.correctAnswer;
    if (isCorrect) {
      this.score++;
    }

    // Store the response
    this.userResponses.push({
      questionId: this.currentQuestion.id,
      question: this.currentQuestion.question,
      selectedOption: this.currentQuestion.options[this.selectedAnswer],
      correctOption: this.currentQuestion.options[this.currentQuestion.correctAnswer],
      isCorrect: isCorrect,
      timeSpent: timeSpent
    });

    // Move to next question after 2 seconds
    setTimeout(() => {
      this.nextQuestion();
    }, 2000);
  }

  /**
   * Auto-submits answer when timer runs out
   */
  private autoSubmitAnswer(): void {
    this.stopTimer();
    this.answerSubmitted = true;

    // Store the response (no answer selected - time ran out)
    this.userResponses.push({
      questionId: this.currentQuestion.id,
      question: this.currentQuestion.question,
      selectedOption: null,
      correctOption: this.currentQuestion.options[this.currentQuestion.correctAnswer],
      isCorrect: false,
      timeSpent: 15
    });

    // Move to next question after 2 seconds
    setTimeout(() => {
      this.nextQuestion();
    }, 2000);
  }

  /**
   * Moves to the next question or completes the interview
   */
  private nextQuestion(): void {
    if (this.currentQuestionIndex < this.questions.length - 1) {
      this.currentQuestionIndex++;
      this.loadQuestion();
    } else {
      this.completeInterview();
    }
  }

  /**
   * Completes the interview and shows results
   */
  private completeInterview(): void {
    this.interviewCompleted = true;
    this.stopTimer();

    // Log the complete interview response
    this.logInterviewResults();
  }

  /**
   * Logs the complete interview results to console
   */
  private logInterviewResults(): void {
    const totalTimeSpent = this.userResponses.reduce((sum, response) => sum + response.timeSpent, 0);
    const correctAnswers = this.userResponses.filter(r => r.isCorrect).length;
    const wrongAnswers = this.userResponses.filter(r => !r.isCorrect && r.selectedOption !== null).length;
    const skippedAnswers = this.userResponses.filter(r => r.selectedOption === null).length;

    const interviewResults = {
      candidate: {
        position: this.interviewInfo.position,
        company: this.interviewInfo.company
      },
      summary: {
        totalQuestions: this.questions.length,
        correctAnswers: correctAnswers,
        wrongAnswers: wrongAnswers,
        skippedAnswers: skippedAnswers,
        score: this.score,
        passingScore: this.interviewInfo.passingScore,
        passed: this.hasPassed,
        totalTimeSpent: totalTimeSpent,
        averageTimePerQuestion: (totalTimeSpent / this.questions.length).toFixed(2)
      },
      detailedResponses: this.userResponses.map((response, index) => ({
        questionNumber: index + 1,
        questionId: response.questionId,
        question: response.question,
        selectedAnswer: response.selectedOption || 'Not Answered',
        correctAnswer: response.correctOption,
        isCorrect: response.isCorrect,
        timeSpent: response.timeSpent + ' seconds'
      })),
      timestamp: new Date().toISOString()
    };

    console.log('='.repeat(80));
    console.log('INTERVIEW COMPLETED - RESULTS');
    console.log('='.repeat(80));
    console.log(JSON.stringify(interviewResults, null, 2));
    console.log('='.repeat(80));

    // Also log as a table for better readability
    console.table(interviewResults.detailedResponses);
  }


  /**
   * Gets the current question object
   */
  public get currentQuestion(): Question {
    return this.questions[this.currentQuestionIndex];
  }

  /**
   * Checks if an option is the correct answer
   * @param index - The index of the option
   */
  public isCorrectAnswer(index: number): boolean {
    return this.answerSubmitted && index === this.currentQuestion.correctAnswer;
  }

  /**
   * Checks if an option is the wrong answer
   * @param index - The index of the option
   */
  public isWrongAnswer(index: number): boolean {
    return this.answerSubmitted &&
           this.selectedAnswer === index &&
           index !== this.currentQuestion.correctAnswer;
  }

  /**
   * Determines if the candidate passed the interview
   */
  public get hasPassed(): boolean {
    return this.score >= this.interviewInfo.passingScore;
  }
}
